import { Component, forwardRef, input, computed, viewChild, ElementRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** Date format pattern: DD-MM-YYYY */
const DATE_PATTERN = /^(\d{2})-(\d{2})-(\d{4})$/;

@Component({
    selector: 'app-date-picker',
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatePickerComponent),
            multi: true,
        },
    ],
})
export class DatePickerComponent implements ControlValueAccessor {
    // Inputs
    readonly placeholder = input<string>('DD-MM-YYYY');
    readonly isError = input<boolean>(false);
    readonly size = input<'xs' | 'sm' | 'md' | 'lg' | 'xl'>('md');
    readonly name = input<string>('');
    readonly disabled = input<boolean>(false);
    readonly min = input<string | undefined>(undefined);
    readonly max = input<string | undefined>(undefined);

    // ViewChild reference to the input
    private readonly dateInput = viewChild<ElementRef<HTMLInputElement>>('dateInput');

    // Internal state
    protected internalValue = '';
    protected displayValue = '';
    protected readonly inputId = `date-picker-${Math.random().toString(36).slice(2, 9)}`;

    // Convert min/max from DD-MM-YYYY to YYYY-MM-DD for native input
    protected readonly internalMin = computed(() => this.toIsoFormat(this.min()));
    protected readonly internalMax = computed(() => this.toIsoFormat(this.max()));

    // ControlValueAccessor callbacks
    private onChange: (value: string) => void = () => { };
    private onTouched: () => void = () => { };

    /**
     * Converts DD-MM-YYYY to YYYY-MM-DD (ISO format for native date input)
     */
    private toIsoFormat(date: string | undefined): string {
        if (!date) return '';
        const parts = date.split('-');
        return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : '';
    }

    /**
     * Converts YYYY-MM-DD to DD-MM-YYYY (display format)
     */
    private toDisplayFormat(isoDate: string): string {
        if (!isoDate) return '';
        const parts = isoDate.split('-');
        return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : '';
    }

    /**
     * Handles native date input change event
     */
    onDateChange(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.internalValue = value;
        this.displayValue = this.toDisplayFormat(value);
        this.onChange(this.displayValue);
    }

    onBlur(): void {
        this.onTouched();
    }

    /**
     * Opens the native calendar picker
     */
    openCalendar(): void {
        if (this.disabled()) return;
        const input = this.dateInput()?.nativeElement;
        if (input?.showPicker) {
            try {
                input.showPicker();
            } catch {
                // Safari may reject showPicker() - the label's [for] attribute
                // will focus the input as a fallback, which opens the picker natively
            }
        }
    }

    // ControlValueAccessor implementation
    writeValue(value: string): void {
        if (!value) {
            this.internalValue = '';
            this.displayValue = '';
            return;
        }

        // Detect format and normalize
        if (DATE_PATTERN.test(value)) {
            // DD-MM-YYYY format
            this.displayValue = value;
            this.internalValue = this.toIsoFormat(value);
        } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            // YYYY-MM-DD format
            this.internalValue = value;
            this.displayValue = this.toDisplayFormat(value);
        } else {
            this.internalValue = '';
            this.displayValue = '';
        }
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        // Handled via input signal
    }
}
